import { Alert, Button, Link, Spinner } from '@heroui/react';
import dayjs from 'dayjs';
import _ from 'lodash';
// import { useTheme } from 'next-themes';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { Footer } from '@/components/layout/Footer';
import { useDismissable } from '@/context/DismissableContext';
import useHospitalFlags from '@/hooks/useHospitalFlags';
import { useGetPatientQuery } from '@/patient-app-common/api/patientApi';
import useAppEvents from '@/patient-app-common/hooks/useAppEvents';
import usePatientDates from '@/patient-app-common/hooks/usePatientDates';
import { getURLParam } from '@/patient-app-common/utils';
import { useAppSelector } from '@/redux/hooks';

import { EduChatLink } from './prompts/EduChatLink';
import { ChronicProgram } from './programs/chronic/ChronicProgram';
import { MaternityProgram } from './programs/maternity/MaternityProgram';
import { SurgicalProgram } from './programs/surgical/SurgicalProgram';
import { MessageList } from './messages/MessageList';
import { MetricEventList } from './messages/MetricEventList';
import { TrackerEventList } from './messages/TrackerEventList';
import { HomeNav } from './HomeNav';

export function Home() {
  const { t } = useTranslation('home');
  // const { theme } = useTheme();

  const { programPhase, isLoading: isLoadingAppEvents } = useAppEvents();
  const { data: patient, isLoading: isLoadingPatient } = useGetPatientQuery();
  const isLoading = isLoadingPatient || isLoadingAppEvents;

  const { patientDates, sameTimeZone } = usePatientDates();

  const ProgramCard = useMemo(() => {
    if (!patient || !patientDates) {
      return null;
    }

    const { maternityProgram, nonSurgicalProgram } = patient;

    if (nonSurgicalProgram) {
      return <ChronicProgram patient={patient} patientDates={patientDates} />;
    }

    if (maternityProgram) {
      return (
        <MaternityProgram
          patient={patient}
          patientDates={patientDates}
          programPhase={programPhase}
        />
      );
    }

    return (
      <SurgicalProgram
        patient={patient}
        patientDates={patientDates}
        programPhase={programPhase}
      />
    );
  }, [patient, patientDates, programPhase]);

  return (
    <div
    // className={`bg-custom_${theme} pt-8`}
    >
      <div className="w-full lg:w-[600px] mx-auto min-h-[100dvh] space-y-4 pt-8 pb-24">
        {isLoading && (
          <div className="items-center flex flex-col justify-center">
            <Spinner className="m-4" color="primary" size="lg" />
            <h1 className="text-lg">{t('common.text.loading')}</h1>
          </div>
        )}
        {!_.isEmpty(patient) && (
          <>
            <ZoneAlert
              hospitalDisplayName={patient.hospitalDisplayName}
              sameTimeZone={sameTimeZone}
              timezone={patient.timezone}
            />
            {ProgramCard}
            <OntarioTrustLink
              hospitalDisplayName={patient.hospitalDisplayName}
              integration={patient.integration}
            />
            <EduChatLink patient={patient} />
            <HomeNav />
            <MetricEventList />
            <TrackerEventList />
            <MessageList />
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}

interface ZoneAlertProps {
  hospitalDisplayName: string;
  sameTimeZone: boolean | null;
  timezone: string;
}

export function ZoneAlert({
  hospitalDisplayName,
  sameTimeZone,
  timezone,
}: ZoneAlertProps) {
  const { t } = useTranslation('home');

  const hospitalZone = dayjs().tz(timezone).format('z');

  if (sameTimeZone) {
    return null;
  }

  return (
    <Alert
      color="warning"
      hideIconWrapper
      isVisible
      title={t('zonealert.description', { hospitalDisplayName, hospitalZone })}
      variant="faded"
    />
  );
}

interface OntarioTrustLinkProps {
  hospitalDisplayName: string;
  integration: undefined | { ontarioTrustedAccount: boolean };
}

export function OntarioTrustLink({
  hospitalDisplayName,
  integration,
}: OntarioTrustLinkProps) {
  const DISMISS_ID = 'ota_card';
  const { persistentDismissed, dismissAlert } = useDismissable();

  const location = useLocation();
  const { hasOntarioTrustedAccount } = useHospitalFlags();
  const { route } = useAppSelector(({ app }) => app);

  let link = `${route}/integration/identos/niagarahealthnavigator/start`;
  const client = getURLParam(location.search, 'client');

  if (client) {
    link += `?client=${client}`;
  }

  if (!hasOntarioTrustedAccount) {
    return null;
  }

  if (location.search.includes('first_ota_auth=true')) {
    return (
      <Alert
        color="primary"
        description="You can now login with your Ontario trusted account or your SeamlessMD account."
        hideIconWrapper
        isVisible={!persistentDismissed[DISMISS_ID]}
        title="Your Ontario trusted account has been linked!"
        variant="faded"
      >
        <Button
          color="primary"
          className="mt-4"
          onPress={() => dismissAlert(DISMISS_ID, true)}
          size="sm"
          variant="flat"
        >
          OK, got it
        </Button>
      </Alert>
    );
  }

  if (!integration?.ontarioTrustedAccount) {
    return (
      <>
        <Alert
          color="primary"
          className="px-12"
          hideIcon
          isVisible={!persistentDismissed[DISMISS_ID]}
          title="Opt to connect to your Ontario Trusted Account"
          variant="faded"
        >
          <div>
            <p className="font-light mt-4 text-sm">
              You can choose to login to SeamlessMD with an Ontario Trusted
              Account, which allows a smoother sign in experience from your
              mobile device.
            </p>
            <p className="font-light mt-4 text-sm">
              This account uses your{' '}
              <strong>financial, online banking login</strong> to verify your
              identity and works well for people who are also using other
              digital health applications at {hospitalDisplayName}. This is
              completely <strong> optional,</strong> and you can still
              enroll/log in with your email address and password.
            </p>
          </div>

          <div className="mt-4 inline-flex gap-2">
            <Button as={Link} href={link} color="primary" size="sm">
              Link my account
            </Button>
            <Button
              onPress={() => dismissAlert(DISMISS_ID, true)}
              size="sm"
              variant="light"
            >
              Dismiss
            </Button>
          </div>
        </Alert>
      </>
    );
  }

  return null;
}
